// Client for our OpenAI-Responses-compatible endpoint.
//
// Deliberately hand-rolled rather than pulling in the OpenAI JS SDK: we need
// the bearer token from keycloak, our own error envelope, and the vendor events
// the SDK would drop.

import { getAuthToken } from './api';

const RESPONSES_URL = '/api/v1/chat/responses';
const TOOL_PREFIX = 'x-operator-plus.tool.';

async function authHeaders(extra = {}) {
  const token = await getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...extra };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

// Our errors come back as {"error": {...}}; anything else is a plain failure.
async function failureFrom(response) {
  const body = await response.json().catch(() => null);
  const message =
    body?.error?.Message || body?.error?.message || body?.message || `request failed (${response.status})`;

  return new Error(message);
}

// Splits an SSE body into {event, data} frames. Frames can straddle chunk
// boundaries, hence the buffer.
async function* readEvents(body, signal) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let split;
      while ((split = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);

        let event = '';
        let data = '';

        for (const line of frame.split('\n')) {
          if (line.startsWith('event: ')) event = line.slice(7);
          else if (line.startsWith('data: ')) data += line.slice(6);
        }

        // a ": ping" comment has no event name
        if (!event) continue;

        yield { event, data: data ? JSON.parse(data) : null };
      }
    }
  } finally {
    reader.cancel().catch(() => {});
    if (signal?.aborted) {
      // nothing to clean up, the fetch is already torn down
    }
  }
}

// streamResponse yields typed chunks as they arrive and returns the final
// response object. Throws on a failed run, with the message the server sent.
//
// Chunks:
//   {kind:'text', delta, itemId}
//   {kind:'reasoning', delta, itemId}
//   {kind:'progress', phase, message}
//   {kind:'tool', state, callId, name, source, provider, args, result, message, error}
export async function* streamResponse({ model, input, conversation, approval, signal }) {
  const response = await fetch(RESPONSES_URL, {
    method: 'POST',
    headers: await authHeaders({ Accept: 'text/event-stream' }),
    body: JSON.stringify({
      model,
      stream: true,
      // an answer to a question is not a new question: the server rejects a
      // request carrying both rather than guessing which one was meant
      ...(approval ? { approval } : { input }),
      ...(conversation ? { conversation } : {}),
    }),
    signal,
  });

  if (!response.ok) {
    throw await failureFrom(response);
  }

  let final = null;

  for await (const { event, data } of readEvents(response.body, signal)) {
    if (event.startsWith(TOOL_PREFIX)) {
      yield {
        kind: 'tool',
        state: event.slice(TOOL_PREFIX.length),
        callId: data.call_id,
        name: data.name,
        source: data.source,
        provider: data.provider,
        args: data.args,
        result: data.result,
        message: data.message,
        error: data.error,
      };
      continue;
    }

    switch (event) {
      // item_id travels with the delta: two adjacent reasoning blocks are two
      // items, and without it they'd be appended into one
      case 'response.output_text.delta':
        yield { kind: 'text', delta: data.delta, itemId: data.item_id };
        break;

      case 'response.reasoning_text.delta':
        yield { kind: 'reasoning', delta: data.delta, itemId: data.item_id };
        break;

      case 'x-operator-plus.progress':
        yield { kind: 'progress', phase: data.phase, message: data.message };
        break;

      // The run stopped to ask something. Not an error: the answer is
      // unfinished and finishing it needs the reader.
      case 'x-operator-plus.interrupted':
        yield {
          kind: 'interrupt',
          interrupt: {
            kind: data.kind,
            message: data.message,
            tool_call_id: data.tool_call_id,
            tool_name: data.tool_name,
          },
        };
        break;

      // A file the run produced. Shaped like the stored attachment so both
      // paths reach the same renderer, and the url is minted per event.
      case 'x-operator-plus.attachment.produced':
        yield {
          kind: 'attachment',
          file: {
            id: data.id,
            filename: data.filename,
            media_type: data.media_type,
            bytes: data.bytes,
            width: data.width,
            height: data.height,
            url: data.url,
          },
        };
        break;

      case 'response.completed':
        final = data.response;
        break;

      case 'error':
        throw runError(data);

      case 'response.failed':
        // Normally the error event arrives first and has already thrown. This
        // is the belt-and-braces path: a failed lifecycle terminal on its own
        // must not read as a finished answer.
        throw runError(data.response?.error);

      default:
        // response.created and the item/part lifecycle: the parts array is
        // built from the deltas, so these need no handling
        break;
    }
  }

  // No terminal event means the stream was cut short: a proxy timeout, a
  // truncated body, a crash. Silence would leave a partial answer looking
  // finished.
  if (!final) {
    throw new Error('the connection ended before the response finished');
  }

  return final;
}

// runError is thrown as assistant-ui's own error shape, which it keeps as is:
// an Error would be cut down to its message, and the detail with it.
function runError(error) {
  return {
    code: error?.code || 'unknown',
    message: error?.message || 'the run failed',
    detail: error?.detail,
  };
}

export async function createResponse({ model, input, conversation, signal }) {
  const response = await fetch(RESPONSES_URL, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      model,
      input,
      ...(conversation ? { conversation } : {}),
    }),
    signal,
  });

  if (!response.ok) {
    throw await failureFrom(response);
  }

  return response.json();
}

// The assistant's text, wherever it ended up in the response object.
export function textOf(response) {
  const parts = response?.output?.flatMap((item) => item.content || []) || [];

  return parts
    .filter((part) => part.type === 'output_text')
    .map((part) => part.text)
    .join('');
}
